import type {
  AutoDealerLeaf,
  GeoCoordinates,
  IdReference,
  PostalAddress,
} from "schema-dts";
import type { ChannelUnit } from "../../s3";
import { resolveMainPhoneNumber } from "../../cms/channelUnitsSource";
import { buildContactPoints, toTelephone } from "../helpers/contactPoints";
import { resolveCoordinates } from "../helpers/geo";
import { dealerId, organizationId } from "../helpers/ids";
import { getUnitDisplayName } from "../../unit";
import type { SchemaBuilder, StructuredDataContext } from "../types";

function buildAddress(unit: ChannelUnit): PostalAddress {
  const streetAddress = [unit.address, unit.number].filter(Boolean).join(", ");

  return {
    "@type": "PostalAddress",
    streetAddress,
    addressLocality: unit.city ?? undefined,
    addressRegion: unit.state ?? undefined,
    postalCode: unit.zipCode ?? undefined,
    addressCountry: unit.country ?? undefined,
  };
}

function buildGeo(unit: ChannelUnit): GeoCoordinates | undefined {
  const coordinates = resolveCoordinates({
    latitude: unit.latitude,
    longitude: unit.longitude,
    mapLink: unit.mapLink,
  });

  if (!coordinates) return undefined;

  return {
    "@type": "GeoCoordinates",
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  };
}

/**
 * Nó completo da loja física. Sai apenas para as unidades listadas em
 * `fullUnitIds`, com o mesmo `@id` usado no `subOrganization` da `Organization`.
 */
export const buildAutoDealers: SchemaBuilder = (
  context: StructuredDataContext,
) => {
  const wanted = new Set(context.fullUnitIds);
  const siteOrganization: IdReference = {
    "@id": organizationId(context.channelPublicId),
  };

  return context.units
    .filter((unit) => wanted.has(unit.id))
    .map((unit): AutoDealerLeaf => ({
      "@type": "AutoDealer",
      "@id": dealerId(unit.publicId || unit.id),
      name: getUnitDisplayName({ unit }),
      legalName: unit.corporateName ?? undefined,
      taxID: unit.cnpj ?? undefined,
      url: context.unitUrls?.[unit.id],
      telephone: toTelephone(resolveMainPhoneNumber(unit)),
      email: unit.email ?? undefined,
      contactPoint: buildContactPoints(unit),
      image: unit.imageUrl ?? undefined,
      logo: unit.logoUrl ?? undefined,
      address: buildAddress(unit),
      geo: buildGeo(unit),
      hasMap: unit.mapLink ?? undefined,
      sameAs: (unit.socialMedia ?? []).map((social) => social.url),
      parentOrganization: siteOrganization,
    }));
};
